import { useState, type FormEvent } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const idleLogout = searchParams.get('reason') === 'idle';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? 'Invalid email or password.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy-900 px-4">
      <div className="w-full max-w-sm">
        {/* Logo & heading */}
        <div className="mb-8 text-center">
          <img src="/logo.png" alt="Ashcourt" className="mx-auto mb-5 h-12 w-auto" />
          <h1 className="text-2xl font-bold text-white">Ashcourt Cashflow</h1>
          <p className="mt-1 text-sm text-slate-400">Cash flow classification &amp; reporting</p>
        </div>

        {idleLogout && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            You were signed out after 10 minutes of inactivity. Please sign in again.
          </div>
        )}

        {/* Form card */}
        <div className="rounded-xl border border-slate-700 bg-navy-800 p-8 shadow-lg [&_.input-field]:bg-navy-900 [&_.input-field]:border-slate-600 [&_.input-field]:text-white [&_.input-field]:placeholder-slate-400 [&_label]:text-slate-200">
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            {error && (
              <div
                role="alert"
                className="rounded-lg border border-red-800 bg-red-900/30 px-4 py-3 text-sm text-red-400"
              >
                {error}
              </div>
            )}

            <Input
              label="Email address"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />

            <Input
              label="Password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />

            <div className="flex items-center justify-end">
              <Link
                to="/forgot-password"
                className="text-sm text-primary-400 hover:text-primary-300"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" loading={loading} className="w-full">
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
